import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, ChevronRight, ChevronLeft, SkipForward, Play } from 'lucide-react';
import { useTheme } from '../../context/ThemeContext';

export interface GuideStep {
  id: string;
  target: string; // CSS selector for the element to highlight
  title: string;
  description: string;
  position?: 'top' | 'bottom' | 'left' | 'right' | 'center';
  action?: 'click' | 'hover' | 'none';
  page?: string; // Which page this step belongs to
}

interface InteractiveGuideProps {
  isVisible: boolean;
  onClose: () => void;
  steps: GuideStep[];
  currentStep: number;
  onStepChange: (step: number) => void;
  onComplete: () => void;
}

export const InteractiveGuide: React.FC<InteractiveGuideProps> = ({ isVisible, onClose, steps, currentStep, onStepChange, onComplete }) => {
  const { theme } = useTheme();
  const [spotlightPosition, setSpotlightPosition] = useState({ x: 0, y: 0, width: 0, height: 0 });
  const [tooltipPosition, setTooltipPosition] = useState({ x: 0, y: 0 });
  const [isTargetVisible, setIsTargetVisible] = useState(false);
  const overlayRef = useRef<HTMLDivElement>(null);
  const tooltipRef = useRef<HTMLDivElement>(null);

  const currentStepData = steps[currentStep];

  // Update spotlight position when step changes
  useEffect(() => {
    if (!isVisible || !currentStepData) return;

    const updatePosition = () => {
      const targetElement = document.querySelector(currentStepData.target);

      if (targetElement) {
        const rect = targetElement.getBoundingClientRect();
        setSpotlightPosition({
          x: rect.left,
          y: rect.top,
          width: rect.width,
          height: rect.height,
        });
        setIsTargetVisible(true);

        // Calculate tooltip position
        const tooltipWidth = 320;
        const tooltipHeight = 200;
        const padding = 20;

        let x = rect.left + rect.width / 2 - tooltipWidth / 2;
        let y = rect.bottom + padding;

        // Adjust position based on step position preference
        switch (currentStepData.position) {
          case 'top':
            y = rect.top - tooltipHeight - padding;
            break;
          case 'left':
            x = rect.left - tooltipWidth - padding;
            y = rect.top + rect.height / 2 - tooltipHeight / 2;
            break;
          case 'right':
            x = rect.right + padding;
            y = rect.top + rect.height / 2 - tooltipHeight / 2;
            break;
          case 'center':
            x = window.innerWidth / 2 - tooltipWidth / 2;
            y = window.innerHeight / 2 - tooltipHeight / 2;
            break;
          default:
            // bottom (default)
            y = rect.bottom + padding;
        }

        // Ensure tooltip stays within viewport
        if (x < padding) x = padding;
        if (x + tooltipWidth > window.innerWidth - padding) {
          x = window.innerWidth - tooltipWidth - padding;
        }
        if (y < padding) y = padding;
        if (y + tooltipHeight > window.innerHeight - padding) {
          y = window.innerHeight - tooltipHeight - padding;
        }

        setTooltipPosition({ x, y });
      } else {
        setIsTargetVisible(false);
      }
    };

    // Initial position update
    updatePosition();

    // Update position on scroll and resize
    const handleUpdate = () => {
      setTimeout(updatePosition, 100);
    };

    window.addEventListener('scroll', handleUpdate, true);
    window.addEventListener('resize', handleUpdate);

    return () => {
      window.removeEventListener('scroll', handleUpdate, true);
      window.removeEventListener('resize', handleUpdate);
    };
  }, [isVisible, currentStep, currentStepData]);

  // Handle step navigation
  const handleNext = () => {
    if (currentStep < steps.length - 1) {
      onStepChange(currentStep + 1);
    } else {
      onComplete();
    }
  };

  const handlePrevious = () => {
    if (currentStep > 0) {
      onStepChange(currentStep - 1);
    }
  };

  const handleSkip = () => {
    onComplete();
  };

  if (!isVisible || !currentStepData) {
    console.log('InteractiveGuide: Not visible or no step data', { isVisible, currentStepData });
    return null;
  }

  console.log('InteractiveGuide: Rendering guide', { currentStep, totalSteps: steps.length, currentStepData });

  return (
    <AnimatePresence>
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 z-50" ref={overlayRef}>
        {/* Dark overlay with spotlight */}
        <div className="relative w-full h-full">
          {/* Dark overlay */}
          <motion.div className="absolute inset-0 bg-black" initial={{ opacity: 0 }} animate={{ opacity: 0.7 }} exit={{ opacity: 0 }} />

          {/* Spotlight */}
          {isTargetVisible && (
            <motion.div
              className="absolute bg-transparent border-4 border-primary rounded-lg shadow-2xl"
              style={{
                left: spotlightPosition.x - 8,
                top: spotlightPosition.y - 8,
                width: spotlightPosition.width + 16,
                height: spotlightPosition.height + 16,
                boxShadow: `0 0 0 9999px rgba(0, 0, 0, 0.7), 0 0 20px rgba(59, 130, 246, 0.5)`,
              }}
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ type: 'spring', damping: 20, stiffness: 300 }}
            />
          )}
        </div>

        {/* Tooltip */}
        <motion.div
          ref={tooltipRef}
          className={`absolute bg-white rounded-xl shadow-2xl border max-w-sm w-80 ${theme === 'dark' ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}
          style={{
            left: tooltipPosition.x,
            top: tooltipPosition.y,
          }}
          initial={{ scale: 0.8, opacity: 0, y: 20 }}
          animate={{ scale: 1, opacity: 1, y: 0 }}
          exit={{ scale: 0.8, opacity: 0, y: 20 }}
          transition={{ type: 'spring', damping: 20, stiffness: 300 }}
        >
          {/* Header */}
          <div className={`p-4 border-b ${theme === 'dark' ? 'border-gray-700' : 'border-gray-200'}`}>
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-2 rtl:space-x-reverse">
                <div className="w-8 h-8 bg-primary rounded-full flex items-center justify-center">
                  <Play className="w-4 h-4 text-white" />
                </div>
                <div>
                  <h3 className={`font-semibold text-sm ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>{currentStepData.title}</h3>
                  <p className={`text-xs ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>
                    خطوة {currentStep + 1} من {steps.length}
                  </p>
                </div>
              </div>
              <button onClick={onClose} className={`p-1 rounded-full hover:bg-gray-100 transition-colors ${theme === 'dark' ? 'hover:bg-gray-700' : 'hover:bg-gray-100'}`}>
                <X className="w-4 h-4 text-gray-500" />
              </button>
            </div>
          </div>

          {/* Content */}
          <div className="p-4">
            <p className={`text-sm leading-relaxed mb-4 ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>{currentStepData.description}</p>

            {/* Progress bar */}
            <div className={`w-full h-2 rounded-full mb-4 ${theme === 'dark' ? 'bg-gray-700' : 'bg-gray-200'}`}>
              <motion.div className="h-full bg-primary rounded-full" initial={{ width: 0 }} animate={{ width: `${((currentStep + 1) / steps.length) * 100}%` }} transition={{ duration: 0.3 }} />
            </div>

            {/* Actions */}
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-2 rtl:space-x-reverse">
                <button
                  onClick={handlePrevious}
                  disabled={currentStep === 0}
                  className={`p-2 rounded-lg transition-colors ${
                    currentStep === 0
                      ? 'text-gray-400 cursor-not-allowed'
                      : theme === 'dark'
                      ? 'text-gray-400 hover:bg-gray-700 hover:text-white'
                      : 'text-gray-500 hover:bg-gray-100 hover:text-gray-700'
                  }`}
                >
                  <ChevronRight className="w-4 h-4" />
                </button>
                <button onClick={handleNext} className="flex items-center space-x-1 rtl:space-x-reverse px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark transition-colors">
                  <span className="text-sm font-medium">{currentStep === steps.length - 1 ? 'إنهاء' : 'التالي'}</span>
                  {currentStep < steps.length - 1 && <ChevronLeft className="w-4 h-4" />}
                </button>
              </div>
              <button
                onClick={handleSkip}
                className={`flex items-center space-x-1 rtl:space-x-reverse px-3 py-2 rounded-lg transition-colors ${
                  theme === 'dark' ? 'text-gray-400 hover:bg-gray-700 hover:text-white' : 'text-gray-500 hover:bg-gray-100 hover:text-gray-700'
                }`}
              >
                <SkipForward className="w-4 h-4" />
                <span className="text-sm">تخطي</span>
              </button>
            </div>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};
